import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { API, invalidateApiCache, setSessionExpiredHandler } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSessionExpiredHandler(() => setUser(false));
    return () => setSessionExpiredHandler(null);
  }, []);

  const checkAuth = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const _verifySession = useCallback(async () => {
    // The login/register endpoints return the user object in the JSON body, but
    // real session persistence depends on HTTP-only cookies. Re-fetch /me so
    // we fail fast here if the browser didn't store/send cookies, instead of
    // navigating to a protected route and immediately being bounced back.
    try {
      const { data } = await API.get("/auth/me");
      return data;
    } catch (err) {
      // Replace the low-level API error with a clear message so the UI doesn't
      // just flash "Not authenticated" after a successful login.
      const sessionError = new Error(
        "We couldn't keep you signed in. Please allow cookies and try again. If this continues, contact support."
      );
      sessionError.cause = err;
      throw sessionError;
    }
  }, []);

  const login = useCallback(
    async (email, password, website = "") => {
      setLoading(true);
      try {
        // Don't trust the login response body as "authenticated" — it only
        // proves the credentials were correct, not that the session cookie
        // was actually stored/sent back. Only set `user` once /auth/me
        // confirms the cookie session works, so ProtectedRoute never renders
        // an authenticated view on unverified state.
        await API.post("/auth/login", { email, password, website });
        const me = await _verifySession();
        setUser(me);
        return me;
      } catch (err) {
        setUser(false);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [_verifySession]
  );

  const register = useCallback(
    async (email, password, name, website = "") => {
      setLoading(true);
      try {
        await API.post("/auth/register", {
          email,
          password,
          name,
          role: "student",
          website,
        });
        const me = await _verifySession();
        setUser(me);
        return me;
      } catch (err) {
        setUser(false);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [_verifySession]
  );

  const logout = useCallback(async () => {
    try {
      await API.post("/auth/logout");
    } finally {
      // Clear local auth state even if the network call fails — the user
      // clicked logout and expects the UI to reflect that regardless.
      invalidateApiCache();
      setUser(false);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, checkAuth }),
    [user, loading, login, register, logout, checkAuth]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};
